import { useState } from 'react';
import { IconX } from '@tabler/icons-react';
import { changePassword } from '../api';
import { useModalA11y } from '../utils/useModalA11y';
import {
    getConfirmPasswordInlineError,
    getPasswordFormatError,
    getPasswordInlineError,
    isValidPasswordFormat,
    PASSWORD_MAX_LENGTH,
    sanitizePasswordInput,
} from '../utils/password';
import FormField from './ui/FormField';
import ErrorMessage from './ui/ErrorMessage';
import { track, trackErrorCode } from '../utils/analytics';
import './ChangePasswordModal.scss';

export default function ChangePasswordModal({ login, onClose, onSuccess }) {
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const dialogRef = useModalA11y(true, onClose);

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const passwordError = getPasswordFormatError(newPassword, { strict: true, login });
        const confirmError = !confirmPassword
            ? 'Повторите пароль'
            : (newPassword !== confirmPassword ? 'Пароли не совпадают' : null);

        if (passwordError || confirmError) {
            setFieldErrors({
                newPassword: passwordError || '',
                confirmPassword: confirmError || '',
            });
            return;
        }

        setLoading(true);
        try {
            await changePassword(oldPassword, newPassword);
            track('password_change_success');
            onSuccess('Пароль успешно изменён');
            onClose();
        } catch (err) {
            trackErrorCode('password_change_error', err.message);
            setError(err.message || 'Не удалось сменить пароль');
        } finally {
            setLoading(false);
        }
    };

    const isValid = oldPassword
        && isValidPasswordFormat(newPassword, { strict: true, login })
        && newPassword === confirmPassword
        && confirmPassword;

    return (
        <div className="change-password-overlay" onClick={handleOverlayClick}>
            <div
                ref={dialogRef}
                className="change-password-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="change-password-title"
            >
                <div className="change-password-modal__header">
                    <h3 id="change-password-title">Сменить пароль</h3>
                    <button
                        type="button"
                        className="change-password-modal__close"
                        onClick={onClose}
                        aria-label="Закрыть"
                    >
                        <IconX size={20} stroke={1.5} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="change-password-modal__form">
                    <ErrorMessage message={error} />

                    <FormField
                        label="Текущий пароль"
                        type="password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(sanitizePasswordInput(e.target.value))}
                        autoComplete="current-password"
                        maxLength={PASSWORD_MAX_LENGTH}
                    />
                    <FormField
                        label="Новый пароль"
                        type="password"
                        value={newPassword}
                        onChange={(e) => {
                            const nextPassword = sanitizePasswordInput(e.target.value);
                            setNewPassword(nextPassword);
                            setFieldErrors((prev) => ({
                                ...prev,
                                newPassword: getPasswordInlineError(nextPassword, { login }),
                                ...(confirmPassword
                                    ? {
                                        confirmPassword: getConfirmPasswordInlineError(
                                            nextPassword,
                                            confirmPassword,
                                        ),
                                    }
                                    : {}),
                            }));
                        }}
                        autoComplete="new-password"
                        maxLength={PASSWORD_MAX_LENGTH}
                        error={fieldErrors.newPassword}
                    />
                    <FormField
                        label="Подтвердите пароль"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => {
                            const nextConfirm = sanitizePasswordInput(e.target.value);
                            setConfirmPassword(nextConfirm);
                            setFieldErrors((prev) => ({
                                ...prev,
                                confirmPassword: getConfirmPasswordInlineError(
                                    newPassword,
                                    nextConfirm,
                                ),
                            }));
                        }}
                        autoComplete="new-password"
                        maxLength={PASSWORD_MAX_LENGTH}
                        error={fieldErrors.confirmPassword}
                    />

                    <button
                        type="submit"
                        className="btn btn--primary btn--block"
                        disabled={!isValid || loading}
                    >
                        {loading ? 'Сохраняем...' : 'Сохранить'}
                    </button>
                </form>
            </div>
        </div>
    );
}
